/**
 * The single answer to "which number from this file row, in which element
 * quantity, for this mapping target".
 *
 * Every consumer — the metadata strip, the Quantity from file picker, the rate
 * label and materialization — reads this one resolution. Resolving it in
 * several places is how the strip came to show metres for a row that
 * materialized off its volume column.
 *
 * The resolution falls straight through, with no back-references:
 *
 *   1. Order the row's candidate sources: a pinned measure first, then the
 *      row's own Quantity/Unit cells, then its remaining IFC measure columns
 *      in the IFC type's priority order.
 *   2. A typed rate short-circuits — it was calibrated against one source, so
 *      it owns it and no alternative may take over.
 *   3. Otherwise take the first source the target can express, and name the
 *      element quantity from that source's unit.
 *   4. No source the target can express → the user must type a rate.
 */
import { ElementCategoryID } from '@shared/models/element-categories.model';
import type { CandidateRow } from '@shared/models/element-import.model';
import {
  ElementQuantityExpressionName,
  elementQuantityUnitRecord,
  iconQuantityNames,
} from '@shared/models/element-quantities.model';
import { ElementKind } from '@shared/models/project.model';
import type { IProduct } from '@shared/models/product.model';
import type { Recipe } from '@shared/models/recipe.model';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import {
  candidateQuantityFrom,
  candidateUnitFrom,
  harmonizeCandidateUnitString,
  parseNumericCell,
} from './candidate-row-cells.helpers';
import {
  canConvertConversionFactors,
  getCorrespondingConversionFactorUnit,
} from './conversion.helpers';
import {
  applyElementCategoryPropertySelections,
  createElementWithCategory,
  getElementCategory,
  getElementCategoryAndSelectionsByProductId,
} from './element-category.helpers';
import { getSelectedQuantityName } from './element-quantity-core.helpers';
import { isElementQuantityExpressionName } from './element-quantity.helpers';
import { candidateIfcMeasures } from './ifc-candidate-measure.helpers';
import type { IfcMeasure } from './ifc-quantity.helpers';
import {
  listAvailableIfcMeasures,
  quantityFromIfcMeasure,
} from './ifc-quantity.helpers';
import { resolveAreaOrientationFromRow } from './import-area-orientation.helpers';
import {
  PRIORITY_BY_CATEGORY,
  UNAMBIGUOUS_UNIT_QUANTITY,
  categoryDefinesQuantity,
} from './import-quantity-priority.helpers';

/** What a row's Mängd is mapped onto. */
export type ImportTarget =
  { kind: 'product'; product: IProduct } | { kind: 'recipe'; recipe: Recipe };

/** One number the row can supply, and where it came from. */
export type ImportQuantitySource = {
  /** The IFC measure column, or `undefined` for the row's own Quantity/Unit cells. */
  measure: IfcMeasure | undefined;
  /** Row-side unit spelling (`m3`, not `m³`) — what mapping entries are keyed by. */
  rowUnit: string;
  quantity: number;
};

export type ImportQuantityResolution =
  | (ImportQuantitySource & {
      status: 'resolved';
      /**
       * Harmonized {@link ImportQuantitySource.rowUnit}. Absent when a typed
       * rate carries a unit the harmonizer does not know (`frp`, `x`) -- the
       * user's number stands, but nothing may claim it is a physical unit.
       */
      unit: ConversionFactorUnit | undefined;
      /** Element quantity the Mängd is written to. */
      quantityName: ElementQuantityExpressionName | undefined;
      factor: number;
      /** `manual` when a typed rate produced the factor. */
      via: 'auto' | 'manual';
    })
  | (ImportQuantitySource & { status: 'needsManualFactor' })
  /** The row supplies no usable number at all. */
  | { status: 'unresolvable' };

export type ResolveImportQuantityParams = {
  row: CandidateRow;
  target: ImportTarget;
  /** The user's Quantity from file pick — a preference, not a lock. */
  pinnedMeasure?: IfcMeasure;
  /** An explicit / persisted element-quantity choice. */
  explicitQuantity?: ElementQuantityExpressionName;
  /** A rate the user typed; owns whichever source it was calibrated against. */
  manualFactor?: number;
};

/** One measure column as a source, when the row carries a usable number. */
const measureSource = (
  row: CandidateRow,
  measure: IfcMeasure,
): ImportQuantitySource | undefined => {
  const { quantity, unit } = quantityFromIfcMeasure(row, measure);
  if (!unit) return undefined;
  const parsed = parseNumericCell(quantity);
  if (parsed === undefined) return undefined;
  return { measure, rowUnit: unit, quantity: parsed };
};

/**
 * The row's sources, best first: the pinned measure, then the row's own cells,
 * then its remaining IFC measures in the parser's own priority order. A pinned
 * measure the row does not carry simply drops out.
 */
export const listImportQuantitySources = (
  row: CandidateRow,
  pinnedMeasure?: IfcMeasure,
): ImportQuantitySource[] => {
  const sources: ImportQuantitySource[] = [];
  const seen = new Set<string>();

  const push = (source: ImportQuantitySource | undefined): void => {
    if (!source || seen.has(source.rowUnit)) return;
    seen.add(source.rowUnit);
    sources.push(source);
  };

  if (pinnedMeasure) push(measureSource(row, pinnedMeasure));

  const ownUnit = candidateUnitFrom(row);
  const ownQuantity = candidateQuantityFrom(row);
  if (ownUnit && ownQuantity !== undefined) {
    push({ measure: undefined, rowUnit: ownUnit, quantity: ownQuantity });
  }

  // The IFC type's own order first, then every remaining column the row
  // carries. The type priority says which measure suits the entity, not which
  // ones exist -- a door lists no volume, but a door row mapped to a column
  // recipe still has one, and the menu already offers it.
  for (const alt of candidateIfcMeasures(row)) {
    push({ measure: alt.measure, rowUnit: alt.unit, quantity: alt.quantity });
  }
  for (const measure of listAvailableIfcMeasures(row)) {
    push(measureSource(row, measure));
  }

  return sources;
};

/** Element quantities a product can be measured in, within its own category. */
export const productQuantityNames = (
  product: IProduct,
): ElementQuantityExpressionName[] => {
  const categoryId =
    getElementCategoryAndSelectionsByProductId(product.id)?.category_id ??
    ElementCategoryID.OtherProduct;

  return iconQuantityNames
    .filter(isElementQuantityExpressionName)
    .filter((name) => {
      const unit = elementQuantityUnitRecord[name];
      const cf = unit
        ? getCorrespondingConversionFactorUnit(unit, false)
        : undefined;
      if (!cf) return false;
      return (
        canConvertConversionFactors(product.conversion_factors, cf) &&
        categoryDefinesQuantity(categoryId, name)
      );
    });
};

/** The product's own category default Amount quantity, when it declares one. */
const productCategoryDefaultQuantity = (
  product: IProduct,
): ElementQuantityExpressionName | undefined => {
  const { category_id, selections } =
    getElementCategoryAndSelectionsByProductId(product.id) ?? {};
  if (!category_id) return undefined;

  let element = createElementWithCategory({
    kind: ElementKind.Element,
    category_id,
  });
  if (selections) {
    element = applyElementCategoryPropertySelections(element, selections);
  }
  const name =
    getSelectedQuantityName(element) ??
    getElementCategory(category_id)?.defaultSelectedQuantity;
  return isElementQuantityExpressionName(name) ? name : undefined;
};

/**
 * Element quantities a recipe category can be imported into, best first.
 *
 * The category's own order leads and is taken as given -- each list is curated
 * to name only quantities its category defines (a wall omits `mass` on
 * purpose). Behind it come the units that name a quantity on their own, so a
 * category with no list, or one whose list no column can fill, still imports a
 * kg or m³ column instead of demanding a typed rate.
 */
export const recipeQuantityNames = (
  recipe: Recipe,
): readonly ElementQuantityExpressionName[] => {
  const categoryId = recipe.category_id;
  if (categoryId === undefined) return [];
  const priority = PRIORITY_BY_CATEGORY[categoryId] ?? [];

  const unambiguous = Object.values(UNAMBIGUOUS_UNIT_QUANTITY).filter(
    (name) =>
      !priority.includes(name) && categoryDefinesQuantity(categoryId, name),
  );
  return [...priority, ...unambiguous];
};

/** The quantities `target` can be measured in, best first. */
const targetQuantityNames = (
  target: ImportTarget,
): readonly ElementQuantityExpressionName[] =>
  target.kind === 'product'
    ? productQuantityNames(target.product)
    : recipeQuantityNames(target.recipe);

const unitOfQuantity = (
  name: ElementQuantityExpressionName,
): ConversionFactorUnit | undefined => {
  const display = elementQuantityUnitRecord[name];
  return display
    ? getCorrespondingConversionFactorUnit(display, false)
    : undefined;
};

/**
 * The element quantity `unit` fills on `target`. Several quantities can share a
 * unit (`area_top` vs `area_side`); the row's own wording breaks the tie, then
 * the target's own default, then the target's order.
 */
const quantityNameForUnit = (
  target: ImportTarget,
  unit: ConversionFactorUnit,
  row: CandidateRow,
): ElementQuantityExpressionName | undefined => {
  const names = targetQuantityNames(target);
  const matching = names.filter((name) => unitOfQuantity(name) === unit);
  const orientation = resolveAreaOrientationFromRow(row);

  if (matching.length === 0) {
    // A recipe has no factors, so a unit that names no quantity cannot fill
    // one. A product converts, so `pcs` on a kg product is still an import --
    // the row's own wording decides, then the product's category default.
    if (target.kind === 'recipe') return undefined;
    if (orientation && names.includes(orientation)) return orientation;
    return productCategoryDefaultQuantity(target.product);
  }
  if (matching.length === 1) return matching[0];

  if (orientation && matching.includes(orientation)) return orientation;
  if (target.kind === 'product') {
    const categoryDefault = productCategoryDefaultQuantity(target.product);
    if (categoryDefault && matching.includes(categoryDefault)) {
      return categoryDefault;
    }
  }
  return matching[0];
};

/**
 * The factor that turns `source.quantity` into `quantityName`'s unit. A product
 * converts through its own factors; a recipe carries none, so the harmonizer's
 * scale (ton → 1000 kg) is the whole conversion.
 */
const autoFactorFor = (
  target: ImportTarget,
  harmonized: { unit: ConversionFactorUnit; quantity: number },
): number | undefined => {
  if (target.kind === 'recipe') return harmonized.quantity;
  const factor = target.product.conversion_factors[harmonized.unit];
  return factor ?? undefined;
};

export const resolveImportQuantity = ({
  row,
  target,
  pinnedMeasure,
  explicitQuantity,
  manualFactor,
}: ResolveImportQuantityParams): ImportQuantityResolution => {
  const sources = listImportQuantitySources(row, pinnedMeasure);
  const [ownSource] = sources;
  if (!ownSource) return { status: 'unresolvable' };

  // A typed rate was calibrated against the source the user was looking at, so
  // it owns it: no alternative column may replace what they entered.
  if (manualFactor !== undefined) {
    const harmonized = harmonizeCandidateUnitString(ownSource.rowUnit);
    return {
      ...ownSource,
      status: 'resolved',
      unit: harmonized?.unit,
      quantityName:
        explicitQuantity ??
        (harmonized
          ? quantityNameForUnit(target, harmonized.unit, row)
          : undefined),
      factor: manualFactor,
      via: 'manual',
    };
  }

  // A recipe carries no conversion factors, so a picked quantity can only be
  // filled by a source already in its unit. A product converts between units,
  // so any source its factors cover can fill any of its quantities.
  const wantedUnit =
    explicitQuantity && target.kind === 'recipe'
      ? unitOfQuantity(explicitQuantity)
      : undefined;

  for (const source of sources) {
    const harmonized = harmonizeCandidateUnitString(source.rowUnit);
    if (!harmonized) continue;
    if (wantedUnit && harmonized.unit !== wantedUnit) continue;

    const quantityName =
      explicitQuantity ?? quantityNameForUnit(target, harmonized.unit, row);
    if (!quantityName) continue;

    const factor = autoFactorFor(target, harmonized);
    if (factor === undefined) continue;

    return {
      ...source,
      status: 'resolved',
      unit: harmonized.unit,
      quantityName,
      factor,
      via: 'auto',
    };
  }

  return { ...ownSource, status: 'needsManualFactor' };
};
